import { CSSProperties, useId } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components/image/src/image";
import { Link } from "@/libraries/design/components/link/src/link";
import { wacToken } from "@/libraries/design/foundations";
import { useDarkTheme } from "@/libraries/design/utils/dark-theme-provider";
import { DataEventProps } from "@/libraries/wpl/foundations/src/data-event-props";

export type CustomerQuoteColors = "blue" | "lime" | "purple" | "orange";

export type CustomerQuoteSurfaceColor = {
    quoteBackground: string;
    imageBackground: string;
    textColor: string;
};

export type CustomerQuoteProps = {
    logoSrc: string;
    logoAlt: string;
    name: string;
    title: string;
    company: string;
    quote: string;
    ctaLinkText?: string;
    ctaLinkUrl?: string;
    ctaLinkDataEventProps?: DataEventProps;
    color: CustomerQuoteColors;
    imageSrc: string;
    imageAlt?: string;
    motionConfig?: {
        transformForward: string;
        transformBackward: string;
    };
};

export const quoteSurfaceColor = (
    color: CustomerQuoteColors
): CustomerQuoteSurfaceColor => {
    const map: Record<CustomerQuoteColors, CustomerQuoteSurfaceColor> = {
        blue: {
            quoteBackground: wacToken("color.extended.blue800"),
            imageBackground: wacToken("color.extended.blue200"),
            textColor: wacToken("color.neutral.light0")
        },
        lime: {
            quoteBackground: wacToken("color.extended.lime800"),
            imageBackground: wacToken("color.extended.lime200"),
            textColor: wacToken("color.neutral.light0")
        },
        purple: {
            quoteBackground: wacToken("color.extended.purple800"),
            imageBackground: wacToken("color.extended.purple200"),
            textColor: wacToken("color.neutral.light0")
        },
        orange: {
            quoteBackground: wacToken("color.extended.orange700"),
            imageBackground: wacToken("color.extended.saffron200"),
            textColor: wacToken("color.neutral.light0")
        }
    };

    return map[color];
};

const customerQuoteStyles = css({
    display: "flex",
    flexDirection: "row-reverse",
    alignItems: "flex-end",
    maxWidth: "992px",
    margin: "0 auto",
    [media.only.md]: {
        maxWidth: "812px"
    },
    [media.only.sm]: {
        maxWidth: "600px"
    },
    [media.below.sm]: {
        maxWidth: "280px"
    },
    // animation hook when in a gallery container with extra motion enabled
    willChange: "transform",
    transform: `translateX(calc(var(--gc-scroll-speed) * var(--gc-item-i, 0) / 9 * 20rem))`,
    ["@container style(--gc-scroll-direction <= 0)"]: {
        transform: `translateX(calc(var(--gc-scroll-speed) * (9 - var(--gc-item-i, 0)) / 9 * -20rem))`
    }
});
const linkImageContainerStyles = css({
    // Need to style anchors differently for a linkable card
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".card-image img": {
        transition: "transform 350ms ease-out"
    },
    ["&:has(.full-card:hover), &:has(.full-card:focus)"]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".card-image img": {
            transition: "transform 350ms ease-in",
            transform: "scale(1.1)"
        }
    }
});

const customerImageStyles = css({
    backgroundColor: "var(--customer-quote-img-bg)",
    mixBlendMode: "multiply",
    width: "360px",
    flexShrink: 0,
    lineHeight: 0,
    margin: `auto calc(-1 * ${token("space.600")}) 0 0`,
    position: "relative",
    borderRadius: wacToken("corner.radius.card"),
    overflow: "hidden",
    aspectRatio: "1/1",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        width: "100%",
        height: "auto",
        display: "block",
        objectFit: "contain",
        objectPosition: "center bottom"
    },
    [media.only.md]: {
        width: "300px"
    },
    [media.only.sm]: {
        width: "240px"
    },
    [media.below.sm]: {
        display: "none"
    },

    // animation hook when in a gallery container with extra motion enabled
    transform: `translateX(clamp(-5rem, calc(var(--gc-scroll-speed) * 4rem), 5rem))`
});

const customerImageDarkThemeStyles = css({
    mixBlendMode: "screen"
});

const customerQuoteContentStyles = css({
    borderRadius: wacToken("corner.radius.card"),
    maxWidth: "680px",
    display: "flex",
    flexGrow: 1,
    flexDirection: "column",
    gap: token("space.150"),
    padding: `${token("space.600")} ${token("space.600")} ${token(
        "space.600"
    )} ${token("space.1000")}`,
    backgroundColor: "var(--customer-quote-bg)",
    color: "var(--customer-quote-text)",
    [media.only.md]: {
        fontSize: token("space.300"),
        maxWidth: "560px",
        padding: `${token("space.600")} ${token("space.600")} ${token(
            "space.600"
        )} ${token("space.1000")}`
    },
    [media.only.sm]: {
        fontSize: token("space.200"),
        maxWidth: "400px",
        padding: `${token("space.500")} ${token("space.500")} ${token(
            "space.500"
        )} ${token("space.800")}`
    },
    [media.below.sm]: {
        fontSize: token("space.100"),
        width: "100%",
        maxWidth: "280px",
        padding: token("space.400")
    }
});
const linkQuoteContainerStyles = css({
    "position": "relative",
    // Need to style anchors differently for a linkable card and cannot pass in CompiledCSS
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [".full-card"]: {
        "::before": {
            content: "",
            inset: "0",
            position: "absolute",
            zIndex: 1
        },
        "&:link, &:visited, &:hover, &:active": {
            "color": "inherit",
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "& span, & > svg": {
                opacity: 1,
                mixBlendMode: "normal"
            }
        }
    },
    // motion on hover
    "&::before": {
        content: "",
        position: "absolute",
        zIndex: -1,
        insetBlockStart: 0,
        insetInline: 0,
        borderStartEndRadius: wacToken("corner.radius.card"),
        borderStartStartRadius: wacToken("corner.radius.card"),
        height: "48px",
        backgroundColor: "inherit",
        transition: "transform 250ms ease-in"
    },
    ["&:has(.full-card:hover), &:has(.full-card:focus)"]: {
        "&::before": {
            transition: "transform 250ms ease-out",
            transform: "translateY(-24px)"
        }
    }
});

const logoStyles = css({
    width: "55%",
    height: "24px",
    objectFit: "contain",
    objectPosition: "0% 50%",
    [media.above.md]: {
        height: "32px"
    }
});

const logoColoringStyles = css({
    filter: "brightness(0) saturate(100%) invert(100%)",
    ["-webkit-filter"]: "brightness(0) saturate(100%) invert(100%)"
});

const quoteIconStyles = css({
    width: "63px",
    height: "54px",
    [media.only.md]: {
        width: "47px",
        height: "40px"
    },
    [media.below.sm]: {
        width: "37px",
        height: "32px"
    }
});

const logoQuoteStackStyles = css({
    display: "flex",
    flexDirection: "column",
    rowGap: token("space.500")
});

export const CustomerQuote = ({
    logoSrc,
    logoAlt,
    name,
    title,
    quote,
    ctaLinkText,
    ctaLinkUrl,
    ctaLinkDataEventProps,
    imageSrc,
    imageAlt,
    color,
    motionConfig
}: CustomerQuoteProps) => {
    const { isDarkTheme } = useDarkTheme();
    const isValidLink = ctaLinkUrl && ctaLinkText;
    const uniqueId = useId();
    const attributionId = `customer-quote-attribution-${uniqueId}`;
    const linkId = `customer-quote-link-${uniqueId}`;

    return (
        <article
            css={[
                customerQuoteStyles,
                isValidLink && linkImageContainerStyles,
                motionConfig?.transformForward &&
                    css({
                        transform: motionConfig?.transformForward,
                        ["@container style(--gc-scroll-direction <= 0)"]: {
                            transform: motionConfig?.transformBackward
                        }
                    })
            ]}
            style={
                {
                    "--customer-quote-bg":
                        quoteSurfaceColor(color).quoteBackground,
                    "--customer-quote-img-bg":
                        quoteSurfaceColor(color).imageBackground,
                    "--customer-quote-text": quoteSurfaceColor(color).textColor
                } as CSSProperties
            }
        >
            <div
                css={[
                    customerQuoteContentStyles,
                    isValidLink && linkQuoteContainerStyles
                ]}
            >
                <div css={logoQuoteStackStyles}>
                    <Image
                        css={[logoStyles, logoColoringStyles]}
                        src={logoSrc}
                        alt={logoAlt}
                        height="40"
                    />

                    <svg
                        css={quoteIconStyles}
                        width="63"
                        height="54"
                        viewBox="0 0 63 54"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M58.2 41.4C58.2 49.2 53.2 54 46 54C38.2 54 32 48.8 32 34C32 14.4 43.2 0.999999 63 0V12.2C54.4 12.8 47 18.2 45 29H46.4C53.6 29 58.2 33.8 58.2 41.4ZM26.2 41.4C26.2 49.2 21.2 54 14 54C6.2 54 0 48.8 0 34C0 14.4 11.2 0.999999 31 0V12.2C22.4 12.8 15 18.2 13 29H14.4C21.6 29 26.2 33.8 26.2 41.4Z"
                            fill="var(--customer-quote-text)"
                        />
                    </svg>
                </div>

                <p
                    className="callout-sm"
                    css={css({
                        marginBlockStart: 0,
                        marginBlockEnd: token("space.100"),
                        color: "var(--customer-quote-text) !important"
                    })}
                >
                    {quote}”
                </p>

                <p
                    id={attributionId}
                    className="body-md"
                    css={css({
                        color: "var(--customer-quote-text) !important"
                    })}
                >
                    {name.toUpperCase()}, {title}
                </p>

                {isValidLink && (
                    <div
                        className="body-md"
                        css={css({
                            color: "var(--customer-quote-text) !important"
                        })}
                    >
                        <Link
                            id={linkId}
                            href={ctaLinkUrl}
                            text={ctaLinkText}
                            ariaLabelledby={`${linkId} ${attributionId}`}
                            aria-label={ctaLinkText}
                            bgColor="bgBlackBlue"
                            type="primary"
                            className="full-card"
                            dataEventProps={ctaLinkDataEventProps}
                        />
                    </div>
                )}
            </div>

            <div
                css={[
                    customerImageStyles,
                    isDarkTheme && customerImageDarkThemeStyles
                ]}
                className="card-image"
            >
                <Image src={imageSrc} alt={imageAlt ?? ""} width="360" />
            </div>
        </article>
    );
};
